import { Alert, BasicSideSheet, Button, Checkbox, Input, Select } from '@components'
import { InputGroup } from '@components/Inputs/InputGroup.tsx'
import { ExpenseClaimRates } from '@query/expenseClaimRates'
import {
  AddressSuggestion,
  ExpenseDistanceSource,
  ExpenseVehicle,
  fetchRouteDistance,
  PerDiemDestination
} from '@query/expenseClaimTrips'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AddressField } from './AddressField'

const VEHICLES: ExpenseVehicle[] = ['CAR', 'MOTORCYCLE', 'MOPED', 'COMPANY_CAR', 'BICYCLE']

export type LegDraft = {
  legId?: number
  position?: number
  followingLegId?: number
  departure_address: string
  destination_address: string
  description: string
  departure_at: string
  arrival_at: string
  vehicle: ExpenseVehicle
  distance_km: string
  route_distance_km: string | null
  distance_source: ExpenseDistanceSource
  departure_place_id: string
  destination_place_id: string
  extra_passengers: string
  has_trailer: boolean
  has_heavy_load: boolean
  country_change: string
}

type LegDrawerProps = {
  slug: string
  draft: LegDraft | null
  canEdit: boolean
  rates?: ExpenseClaimRates
  destinations: PerDiemDestination[]
  error: string | null
  isSaving: boolean
  isDeleting: boolean
  onClose: () => void
  onSubmit: (draft: LegDraft) => void
  onDelete: (legId: number) => void
}

const appliedRate = (rates: ExpenseClaimRates | undefined, rateType: string) =>
  Number(rates?.rates.find(rate => rate.rate_type === rateType)?.applied_amount ?? 0)

const decimal = (value: number) => value.toFixed(2).replace('.', ',')

export const parseKilometres = (raw: string): number | null => {
  const normalized = raw.trim().replace(',', '.')
  if (!/^\d+(\.\d)?$/.test(normalized)) return null
  return Number(normalized)
}

export const vehicleLabel = (t: (key: string) => string, vehicle: ExpenseVehicle) =>
  vehicle === 'BICYCLE'
    ? t('travelAndExpenses.leg.bicycle')
    : t(`settings.expenseClaimRates.rates.${vehicle}`)

const rateParts = (draft: LegDraft, rates?: ExpenseClaimRates) => {
  if (draft.vehicle === 'BICYCLE') return []
  const passengers = Number(draft.extra_passengers) || 0
  return [
    { key: draft.vehicle, amount: appliedRate(rates, draft.vehicle), times: 1 },
    ...(draft.has_trailer && draft.vehicle !== 'COMPANY_CAR'
      ? [{ key: 'TRAILER', amount: appliedRate(rates, 'TRAILER'), times: 1 }]
      : []),
    ...(draft.has_heavy_load
      ? [{ key: 'HEAVY_LOAD', amount: appliedRate(rates, 'HEAVY_LOAD'), times: 1 }]
      : []),
    ...(passengers > 0
      ? [
          {
            key: 'EXTRA_PASSENGER',
            amount: appliedRate(rates, 'EXTRA_PASSENGER'),
            times: passengers
          }
        ]
      : [])
  ]
}

const previewCents = (kilometres: number, rate: number) =>
  Math.round((Math.round(kilometres * 10) * Math.round(rate * 100)) / 10)

export const LegDrawer: React.FC<LegDrawerProps> = ({
  slug,
  draft,
  canEdit,
  rates,
  destinations,
  error,
  isSaving,
  isDeleting,
  onClose,
  onSubmit,
  onDelete
}) => {
  const [t] = useTranslation()
  const [form, setForm] = useState<LegDraft | null>(draft)
  const [distanceError, setDistanceError] = useState<string | null>(null)
  const [routeUnavailable, setRouteUnavailable] = useState(false)
  const distanceEdited = useRef(false)

  useEffect(() => {
    setForm(draft)
    setDistanceError(null)
    setRouteUnavailable(false)
  }, [draft])

  const update = (patch: Partial<LegDraft>) =>
    setForm(current => (current ? { ...current, ...patch } : current))

  const departurePlaceId = form?.departure_place_id
  const destinationPlaceId = form?.destination_place_id

  useEffect(() => {
    if (!canEdit || !departurePlaceId || !destinationPlaceId) return
    distanceEdited.current = false
    let cancelled = false
    fetchRouteDistance(slug, departurePlaceId, destinationPlaceId)
      .then(result => {
        if (cancelled || distanceEdited.current) return
        setRouteUnavailable(result.distance_km === null)
        setForm(current => {
          if (
            !current ||
            current.departure_place_id !== departurePlaceId ||
            current.destination_place_id !== destinationPlaceId
          ) {
            return current
          }
          if (!result.distance_km) {
            return {
              ...current,
              distance_source: 'MANUAL',
              route_distance_km: null,
              distance_km: current.distance_source === 'ROUTE' ? '' : current.distance_km
            }
          }
          return {
            ...current,
            distance_km: result.distance_km.replace('.', ','),
            route_distance_km: result.distance_km,
            distance_source: 'ROUTE'
          }
        })
      })
      .catch(() => {
        if (cancelled || distanceEdited.current) return
        setRouteUnavailable(true)
        setForm(current => {
          if (
            !current ||
            current.departure_place_id !== departurePlaceId ||
            current.destination_place_id !== destinationPlaceId
          ) {
            return current
          }
          return {
            ...current,
            distance_source: 'MANUAL',
            route_distance_km: null,
            distance_km: current.distance_source === 'ROUTE' ? '' : current.distance_km
          }
        })
      })
    return () => {
      cancelled = true
    }
  }, [canEdit, departurePlaceId, destinationPlaceId, slug])

  const changeAddress = (field: 'departure' | 'destination', text: string) => {
    setRouteUnavailable(false)
    const cleared = { distance_source: 'MANUAL' as const, route_distance_km: null }
    if (field === 'departure') {
      update({ ...cleared, departure_address: text, departure_place_id: '' })
      return
    }
    update({ ...cleared, destination_address: text, destination_place_id: '' })
  }

  const selectAddress = (field: 'departure' | 'destination', suggestion: AddressSuggestion) => {
    setRouteUnavailable(false)
    if (field === 'departure') {
      update({ departure_address: suggestion.text, departure_place_id: suggestion.place_id })
      return
    }
    update({ destination_address: suggestion.text, destination_place_id: suggestion.place_id })
  }

  const [arrivalDate = '', arrivalTime = ''] = (form?.arrival_at ?? '').split('T')
  const parts = form ? rateParts(form, rates) : []
  const rate =
    Math.round(parts.reduce((sum, part) => sum + part.amount * part.times, 0) * 100) / 100
  const kilometres = form ? parseKilometres(form.distance_km) : null

  return (
    <BasicSideSheet
      heading={
        form?.legId ? t('travelAndExpenses.leg.editTitle') : t('travelAndExpenses.leg.title')
      }
      isShown={draft !== null}
      onClose={onClose}
      size="md"
    >
      {form && (
        <Form
          onSubmit={event => {
            event.preventDefault()
            if (!canEdit) return
            if (parseKilometres(form.distance_km) === null) {
              setDistanceError(t('travelAndExpenses.leg.distanceInvalid'))
              return
            }
            onSubmit(form)
          }}
        >
          {error && <Alert type="error" title={error} />}
          <AddressField
            slug={slug}
            name="departure_address"
            label={t('travelAndExpenses.leg.departure')}
            info={t('travelAndExpenses.leg.departureHelp')}
            value={form.departure_address}
            disabled={!canEdit}
            onTextChange={text => changeAddress('departure', text)}
            onSelect={suggestion => selectAddress('departure', suggestion)}
          />
          <AddressField
            slug={slug}
            name="destination_address"
            label={t('travelAndExpenses.leg.destination')}
            required
            value={form.destination_address}
            disabled={!canEdit}
            onTextChange={text => changeAddress('destination', text)}
            onSelect={suggestion => selectAddress('destination', suggestion)}
          />
          <Select
            name="country_change"
            label={t('travelAndExpenses.leg.countryChange')}
            value={form.country_change}
            disabled={!canEdit}
            onChange={event => update({ country_change: event.target.value })}
          >
            <option value="">{t('travelAndExpenses.leg.countryChangeNone')}</option>
            {destinations.map(row => (
              <option key={row.key} value={row.key}>
                {row.label}
              </option>
            ))}
          </Select>
          <Input
            name="description"
            label={t('travelAndExpenses.leg.description')}
            placeholder={t('travelAndExpenses.leg.descriptionPlaceholder')}
            value={form.description}
            disabled={!canEdit}
            onChange={event => update({ description: event.target.value })}
          />
          <InputGroup>
            <Input
              name="arrival_date"
              type="date"
              label={t('travelAndExpenses.leg.arrivalDate')}
              required
              value={arrivalDate}
              disabled={!canEdit}
              onChange={event => update({ arrival_at: `${event.target.value}T${arrivalTime}` })}
            />
            <Input
              name="arrival_time"
              type="time"
              label={t('travelAndExpenses.leg.arrivalTime')}
              required
              value={arrivalTime}
              disabled={!canEdit}
              onChange={event => update({ arrival_at: `${arrivalDate}T${event.target.value}` })}
            />
          </InputGroup>
          <Select
            name="vehicle"
            label={t('travelAndExpenses.leg.vehicle')}
            value={form.vehicle}
            disabled={!canEdit}
            onChange={event => {
              const vehicle = event.target.value as ExpenseVehicle
              update({
                vehicle,
                has_trailer: vehicle === 'COMPANY_CAR' ? false : form.has_trailer
              })
            }}
          >
            {VEHICLES.map(vehicle => (
              <option key={vehicle} value={vehicle}>
                {vehicle === 'BICYCLE'
                  ? `${vehicleLabel(t, vehicle)}, ${t('travelAndExpenses.leg.noAllowance')}`
                  : `${vehicleLabel(t, vehicle)}, ${decimal(appliedRate(rates, vehicle))} €/km`}
              </option>
            ))}
          </Select>
          <InputGroup>
            <Input
              name="distance_km"
              label={t('travelAndExpenses.leg.distance')}
              info={
                form.distance_source === 'ROUTE' || form.route_distance_km
                  ? t('travelAndExpenses.leg.distanceHelp')
                  : undefined
              }
              required
              value={form.distance_km}
              disabled={!canEdit}
              errors={distanceError ? { message: distanceError } : undefined}
              onChange={event => {
                setDistanceError(null)
                setRouteUnavailable(false)
                distanceEdited.current = true
                update({ distance_km: event.target.value, distance_source: 'MANUAL' })
              }}
            />
            <Input
              name="extra_passengers"
              type="number"
              min={0}
              step={1}
              label={t('travelAndExpenses.leg.passengers')}
              value={form.extra_passengers}
              disabled={!canEdit}
              onChange={event => update({ extra_passengers: event.target.value })}
            />
          </InputGroup>
          <Help>
            {routeUnavailable
              ? t('travelAndExpenses.leg.unavailable')
              : form.distance_source === 'ROUTE'
              ? t('travelAndExpenses.leg.fastestRoute')
              : form.route_distance_km
              ? t('travelAndExpenses.summary.editedManually')
              : t('travelAndExpenses.leg.manualHint')}
          </Help>
          {form.distance_source === 'MANUAL' && form.route_distance_km && (
            <Help>
              {t('travelAndExpenses.summary.originalKm', {
                km: form.route_distance_km.replace('.', ',')
              })}
            </Help>
          )}

          <ExtrasTitle>{t('travelAndExpenses.leg.extras')}</ExtrasTitle>
          <Checkbox
            name="has_trailer"
            label={t('travelAndExpenses.leg.trailer', {
              rate: decimal(appliedRate(rates, 'TRAILER'))
            })}
            info={
              form.vehicle === 'COMPANY_CAR'
                ? t('travelAndExpenses.leg.trailerCompanyCar')
                : undefined
            }
            checked={form.has_trailer}
            disabled={!canEdit || form.vehicle === 'COMPANY_CAR'}
            onChange={event => update({ has_trailer: event.target.checked })}
          />
          <Checkbox
            name="has_heavy_load"
            label={t('travelAndExpenses.leg.heavy', {
              rate: decimal(appliedRate(rates, 'HEAVY_LOAD'))
            })}
            checked={form.has_heavy_load}
            disabled={!canEdit}
            onChange={event => update({ has_heavy_load: event.target.checked })}
          />

          <Summary>
            <SummaryLine>
              <span>
                {parts.length === 0
                  ? t('travelAndExpenses.leg.noAllowance')
                  : parts
                      .map(
                        part =>
                          `${t(`settings.expenseClaimRates.rates.${part.key}`)} ${decimal(
                            part.amount
                          )}${part.times > 1 ? ` × ${part.times}` : ''}`
                      )
                      .join(' + ')}
              </span>
              <span>{decimal(rate)} €/km</span>
            </SummaryLine>
            <SummaryLine>
              <strong>{t('travelAndExpenses.leg.total')}</strong>
              <strong>
                {formatEurosWithOpts(kilometres === null ? 0 : previewCents(kilometres, rate))}
              </strong>
            </SummaryLine>
          </Summary>

          {canEdit && (
            <Actions>
              <Button type="submit" intent="primary" showSpinner={isSaving}>
                {t('travelAndExpenses.leg.save')}
              </Button>
              {form.legId && (
                <Button
                  type="button"
                  intent="danger"
                  isSecondary
                  showSpinner={isDeleting}
                  onClick={() => onDelete(form.legId as number)}
                >
                  {t('travelAndExpenses.leg.delete')}
                </Button>
              )}
            </Actions>
          )}
        </Form>
      )}
    </BasicSideSheet>
  )
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md}rem;
  overflow: auto;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const Help = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const ExtrasTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.primary}33;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.primary}0d;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-variant-numeric: tabular-nums;
`

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
